// Canonical input -> exact SSM request body.
//
// Field shapes follow @ssm/client's zod schemas (packages/ssm-client/src/schemas)
// so a body built here parses on the way out. Cents become dollars here, and
// the fixed/forbidden fields are set here rather than by callers — a caller
// cannot opt into a payment method or override a unit's status.

import { centsToDollars } from '../../money';
import { UNRESOLVED } from '../../types';
import type {
  EnsureTenantInput,
  MoveInInput,
  PmsRequestEnvelope,
  RecordPaymentInput,
  SsmFacilityConfig,
} from '../../types';
import { assertSafeSsmRequest } from './guard';

const PATHS = {
  AddUpdateAccount: '/api/OnlineRental/AddUpdateAccount',
  ProcessMoveIn: '/api/OnlineRental/ProcessMoveIn',
  ProcessPayment: '/api/OnlineRental/ProcessPayment',
};

/**
 * SSM ids are numeric. Until a real one is stored in ExternalRef we send the
 * sentinel so the shadow record shows plainly what is still unknown, rather
 * than inventing a plausible-looking 0.
 */
function externalNumber(externalID: string | null | undefined): number | string {
  if (externalID == null || externalID === '') return UNRESOLVED;
  const parsed = Number(externalID);
  return Number.isFinite(parsed) ? parsed : UNRESOLVED;
}

/** SSM wants given/family names; our app stores one display name. */
function splitName(name: string): { first: string; last: string } {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { first: 'Unknown', last: 'Unknown' };
  if (parts.length === 1) return { first: parts[0], last: parts[0] };
  return { first: parts[0], last: parts.slice(1).join(' ') };
}

/** SSM dates are datetime strings; our canonical dates may be date-only. */
function toSsmDate(iso: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return `${iso}T00:00:00Z`;
  return iso;
}

function finish(envelope: PmsRequestEnvelope): PmsRequestEnvelope {
  // Guard here as well as before transport: a body cannot leave the mapper
  // unchecked, even in shadow mode where nothing is sent.
  assertSafeSsmRequest(envelope);
  return envelope;
}

export function buildAddUpdateAccount(
  input: EnsureTenantInput,
  cfg: SsmFacilityConfig,
): PmsRequestEnvelope {
  const { first, last } = splitName(input.name);
  const existing = externalNumber(input.externalTenantID);
  const isUpdate = typeof existing === 'number';

  return finish({
    op: 'ensureTenant',
    vendorOp: 'AddUpdateAccount',
    path: PATHS.AddUpdateAccount,
    body: {
      // 0 creates; a real id updates.
      TenantId: isUpdate ? existing : 0,
      Action: isUpdate ? 'U' : 'A',
      FirstName: first,
      LastName: last,
      Email: input.email,
      CellPhoneNumber: input.phone || null,
      Address: input.address.line2
        ? `${input.address.line1} ${input.address.line2}`
        : input.address.line1,
      City: input.address.city,
      State: { Code: input.address.state },
      Zip: input.address.zip,
      CountryId: cfg.countryId,
      FacilityId: cfg.facilityId,
      AccessCode: input.accessCode || null,
    },
  });
}

export function buildProcessMoveIn(input: MoveInInput, cfg: SsmFacilityConfig): PmsRequestEnvelope {
  const amount = centsToDollars(input.amountCents);
  const paymentBlock =
    cfg.paymentMethod === 'check'
      ? {
          checkInput: {
            Amount: amount,
            CheckNumber: `movein:${input.binsUnitID}`,
            AccountHolder: input.tenantName || null,
            BankName: cfg.checkBankName,
            CheckDate: toSsmDate(input.startDateISO),
          },
        }
      : { cashInput: { Amount: amount } };

  return finish({
    op: 'moveIn',
    vendorOp: 'ProcessMoveIn',
    path: PATHS.ProcessMoveIn,
    body: {
      FacilityId: cfg.facilityId,
      TenantId: externalNumber(input.externalTenantID),
      UnitId: externalNumber(input.externalUnitID),
      // Hardcoded by config; never caller-supplied.
      PaymentMethod: cfg.paymentMethod,
      // SSM's docs: pass null when no insurance scheme is selected.
      InsuranceId: cfg.insuranceId,
      StartDate: toSsmDate(input.startDateISO),
      // Our rate, from the pricing system — overrides SSM's board rate so both
      // ledgers agree from day one.
      RentAmount: centsToDollars(input.rentAmountCents),
      AccessCode: input.accessCode || null,
      AdSource: cfg.adSource,
      MediumDesc: cfg.mediumDesc,
      InquirySource: cfg.inquirySource,
      DiscountId: [],
      // Valet fees are our revenue and would exceed SSM's amount due.
      FeesId: [],
      // Must stay false: true would allow a move-in onto an occupied unit.
      OverrideUnitStatus: false,
      ...paymentBlock,
    },
  });
}

export function buildProcessPayment(
  input: RecordPaymentInput,
  cfg: SsmFacilityConfig,
): PmsRequestEnvelope {
  const amount = centsToDollars(input.amountCents);
  const paymentBlock =
    cfg.paymentMethod === 'check'
      ? {
          checkInput: {
            Amount: amount,
            // CheckNumber is a string in SSM's schema, so the real Stripe id
            // fits — this is the two-sided audit trail.
            CheckNumber: input.reference,
            AccountHolder: input.tenantName || null,
            BankName: cfg.checkBankName,
            CheckDate: toSsmDate(input.paidAtISO),
          },
        }
      : { cashInput: { Amount: amount } };

  return finish({
    op: 'recordPayment',
    vendorOp: 'ProcessPayment',
    path: PATHS.ProcessPayment,
    body: {
      FacilityId: cfg.facilityId,
      TenantId: externalNumber(input.externalTenantID),
      UnitId: externalNumber(input.externalUnitID),
      PaymentMethod: cfg.paymentMethod,
      FeesId: [],
      ...paymentBlock,
    },
  });
}

/**
 * Which vendor id fields still hold the sentinel. Phase 1 has no populated
 * ExternalRef rows, so this is how a shadow record states honestly which parts
 * of the body are not yet real.
 */
export function unresolvedFields(envelope: PmsRequestEnvelope): string[] {
  const body = envelope.body;
  if (typeof body !== 'object' || body === null) return [];
  const record = body as Record<string, unknown>;
  return Object.keys(record).filter((key) => record[key] === UNRESOLVED);
}
